// Pure, unit-tested pre-send deliverability linter. Catches the common content
// issues that hurt inbox placement or look like spam — surfaced before a send so
// fixing them is a no-brainer. (Address + unsubscribe are auto-appended by the
// backend, so those aren't checked here.)

const SPAM_PHRASES = [
  "act now", "limited time", "click here", "buy now", "order now",
  "100% free", "risk-free", "risk free", "winner", "congratulations",
  "cash bonus", "guarantee", "$$$", "double your", "earn extra",
  "no cost", "miracle", "viagra",
];

const VALID_TAG = /^\{\{\s*\.(Contact|UserVars|SystemVars)\.[A-Za-z0-9_]+\s*\}\}$/;

function stripHtml(html) {
  return String(html || "").replace(/<[^>]*>/g, " ");
}

function unique(arr) {
  return Array.from(new Set(arr));
}

// Returns a list of { level: "error" | "warn", message } issues. Empty = clean.
export function checkDeliverability({ subject = "", html = "", text = "" } = {}) {
  const issues = [];
  const sub = String(subject).trim();

  if (!sub) {
    issues.push({ level: "error", message: "Subject line is empty." });
  } else {
    if (sub.length > 60) {
      issues.push({ level: "warn", message: `Subject is ${sub.length} characters — keep it under ~60 so it isn't truncated.` });
    }
    if (/[A-Z]/.test(sub) && sub === sub.toUpperCase()) {
      issues.push({ level: "warn", message: "Subject is ALL CAPS, which reads as spam." });
    }
    if ((sub.match(/!/g) || []).length >= 3) {
      issues.push({ level: "warn", message: "Subject has lots of exclamation marks." });
    }
  }

  const haystack = (sub + " " + (text || stripHtml(html))).toLowerCase();
  const hits = SPAM_PHRASES.filter((w) => haystack.includes(w));
  if (hits.length) {
    issues.push({ level: "warn", message: `Spam-trigger phrases found: ${hits.slice(0, 5).join(", ")}.` });
  }

  // Unrecognized merge tags ({{ ... }} that aren't a supported .Contact/.UserVars/.SystemVars field).
  const tags = (html + " " + text + " " + sub).match(/\{\{\s*[^}]+?\s*\}\}/g) || [];
  const bad = unique(tags.filter((t) => !VALID_TAG.test(t)));
  if (bad.length) {
    issues.push({ level: "error", message: `Unrecognized merge tags won't render: ${bad.slice(0, 3).join(", ")}.` });
  }

  const links = (html.match(/<a\b/gi) || []).length;
  if (links > 15) {
    issues.push({ level: "warn", message: `${links} links — too many can trip spam filters.` });
  }

  const imgs = (html.match(/<img\b/gi) || []).length;
  const textLen = stripHtml(html).replace(/\s+/g, "").length;
  if (imgs > 0 && textLen < 100) {
    issues.push({ level: "warn", message: "Mostly images with little text — add real text so it lands in the inbox." });
  }

  return issues;
}
